/**
 * FuarSeriesModal.jsx — Fuar serisi oluştur / düzenle
 *
 * İki yerden açılıyor: Fuar düzenleme formu (fairsform.js, seri seçicinin yanındaki
 * "Yeni Seri" / "Düzenle") ve Fuar Serileri listesi (FuarSeriesListPage.jsx).
 *
 * Alanlar: bağlantı + dil başına (TR/EN) ad ve hikâye. Backend metinleri dil başına
 * UPSERT eder ve gönderilen backstory null ise mevcut metni SİLER — bu yüzden düzenleme
 * ancak serinin tam hâli okunabildiğinde açılıyor.
 *
 * ⚠️ 2026-09-15: `GET /fuar/series/{id}` her seri için 500 dönüyor (backend cast hatası,
 *    bkz. fuarService.js dosya başı). O durumda form, listeden gelen `initial`
 *    (ad + bağlantı) ile salt-okunur açılır ve Kaydet kapalıdır: kaydetmek okuyamadığımız
 *    hikâye metinlerini sessizce silerdi.
 */
import React, { useEffect, useState } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Row, Col, FormGroup, Label, Input, Button, Alert, Spinner, FormFeedback,
} from "reactstrap";
import { fuarService, apiErrorMessage, LANGUAGE } from "../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const EMPTY = { link: "", nameTr: "", backstoryTr: "", nameEn: "", backstoryEn: "" };

export default function FuarSeriesModal({ isOpen, toggle, seriesId = null, initial = null, onSaved }) {
  const { t: tx } = useTranslation("components");
  const isEdit = seriesId != null;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [readFailed, setReadFailed] = useState(false);
  const [touched, setTouched] = useState(false);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!isOpen) return undefined;
    let ignore = false;

    async function run() {
      setError(null);
      setTouched(false);
      setReadFailed(false);
      if (!isEdit) {
        setForm(EMPTY);
        return;
      }
      setForm({
        ...EMPTY,
        link: initial?.link || "",
        nameTr: initial?.nameTr || "",
        nameEn: initial?.nameEn || "",
      });
      setLoading(true);
      try {
        const dto = await fuarService.getFuarSeries(seriesId);
        if (ignore) return;
        const texts = dto?.fuarSeriesTexts || [];
        const tr = texts.find((t) => Number(t.languageId) === LANGUAGE.TR) || {};
        const en = texts.find((t) => Number(t.languageId) === LANGUAGE.EN) || {};
        setForm({
          link: dto?.fuarSeries?.fuarSeriesLink || "",
          nameTr: tr.fuarSeriesName || "",
          backstoryTr: tr.fuarSeriesBackstory || "",
          nameEn: en.fuarSeriesName || "",
          backstoryEn: en.fuarSeriesBackstory || "",
        });
      } catch (e) {
        if (ignore) return;
        setReadFailed(true);
        setError(apiErrorMessage(e, i18n.t("components:fuarSeriesModal.seriDetayiOkunamadi")));
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    run();
    return () => { ignore = true; };
  }, [isOpen, isEdit, seriesId, initial]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const nameTrMissing = !form.nameTr.trim();
  const locked = isEdit && readFailed;

  async function handleSave() {
    setTouched(true);
    if (nameTrMissing || locked) return;
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await fuarService.updateFuarSeries(seriesId, form);
        onSaved?.(seriesId, tx("fuarSeriesModal.seriGuncellendi"));
      } else {
        const out = await fuarService.createFuarSeries(form);
        onSaved?.(out?.seriesId ?? null, tx("fuarSeriesModal.seriOlusturuldu"));
      }
    } catch (e) {
      setError(apiErrorMessage(e, tx("fuarSeriesModal.seriKaydedilemedi")));
    } finally {
      setSaving(false);
    }
  }

  const close = () => !saving && toggle();

  return (
    <Modal isOpen={isOpen} toggle={close} centered size="lg">
      <ModalHeader toggle={close}>
        {isEdit ? tx("fuarSeriesModal.seriyiDuzenle", { seriesId }) : tx("fuarSeriesModal.yeniFuarSerisi")}
      </ModalHeader>
      <ModalBody>
        {locked ? (
          <Alert color="warning" className="small">
            <div className="fw-semibold mb-1">
              <i className="bx bx-error me-1" />{tx("fuarSeriesModal.seriDetayiOkunamadiDuzenlemeKapali")}
            </div>
            {tx("fuarSeriesModal.backend")}{" "}<code>GET /fuar/series/{seriesId}</code>{" "}{tx("fuarSeriesModal.icinHataDondu")}{" "}<em>{error}</em>{tx("fuarSeriesModal.kaydetmekOkunamayanHikayeMetinleriniSilerdi")}
          </Alert>
        ) : (
          error && <Alert color="danger">{error}</Alert>
        )}

        {loading ? (
          <div className="text-center py-4"><Spinner color="primary" /></div>
        ) : (
          <>
            <FormGroup>
              <Label>{tx("fuarSeriesModal.seriBaglantisi")}</Label>
              <Input
                type="url" value={form.link} onChange={set("link")} disabled={locked}
                placeholder="https://emittistanbul.com"
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <h6 className="text-muted text-uppercase fs-12 mb-2">Türkçe</h6>
                <FormGroup>
                  <Label>{tx("fuarSeriesModal.seriAdi")}{" "}<span className="text-danger">*</span></Label>
                  <Input
                    value={form.nameTr} onChange={set("nameTr")} disabled={locked}
                    invalid={touched && nameTrMissing} placeholder={tx("fuarSeriesModal.ornEmitt")}
                  />
                  <FormFeedback>{tx("fuarSeriesModal.turkceSeriAdiZorunludur")}</FormFeedback>
                </FormGroup>
                <FormGroup>
                  <Label>{tx("fuarSeriesModal.hikaye")}</Label>
                  <Input
                    type="textarea" rows={5} value={form.backstoryTr} onChange={set("backstoryTr")}
                    disabled={locked} placeholder={locked ? tx("fuarSeriesModal.okunamadi") : tx("fuarSeriesModal.serininKisaTarihcesi")}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <h6 className="text-muted text-uppercase fs-12 mb-2">English</h6>
                <FormGroup>
                  <Label>Series Name</Label>
                  <Input value={form.nameEn} onChange={set("nameEn")} disabled={locked} placeholder="e.g. EMITT" />
                </FormGroup>
                <FormGroup>
                  <Label>Backstory</Label>
                  <Input
                    type="textarea" rows={5} value={form.backstoryEn} onChange={set("backstoryEn")}
                    disabled={locked} placeholder={locked ? tx("fuarSeriesModal.okunamadi") : "Short history of the series..."}
                  />
                </FormGroup>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={close} disabled={saving}>{tx("fuarSeriesModal.vazgec")}</Button>
        <Button color="primary" onClick={handleSave} disabled={saving || loading || locked}>
          {saving ? <Spinner size="sm" className="me-1" /> : <i className="bx bx-save me-1" />}
          {isEdit ? tx("fuarSeriesModal.guncelle") : tx("fuarSeriesModal.olustur")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
